import { useMemo } from "react";
import { useSelector } from "react-redux";
import { defaultTheme } from "../config/theme";

const isPlainObject = (v) =>
  Boolean(v) && typeof v === "object" && !Array.isArray(v);

// Recurses only into plain objects, so a string override replaces the
// default string and never gets "merged" character by character. Returns the
// base object itself when there is nothing to merge, so a shop without
// overrides renders the exact default theme reference.
export function deepMerge(base, overrides) {
  if (!isPlainObject(overrides)) return base;
  const out = { ...base };
  for (const [key, value] of Object.entries(overrides)) {
    out[key] =
      isPlainObject(value) && isPlainObject(base?.[key])
        ? deepMerge(base[key], value)
        : value;
  }
  return out;
}

// antd draws a drawer's header and footer rules in its split color. The shop
// draws them in the primary color, the same line the app header has under it,
// unless a Drawer split color was set explicitly.
export function withDrawerLine(theme) {
  const primary = theme?.token?.colorPrimary;
  if (!primary || theme?.components?.Drawer?.colorSplit) return theme;
  return {
    ...theme,
    components: {
      ...theme.components,
      Drawer: { ...theme.components?.Drawer, colorSplit: primary },
    },
  };
}

// The Button section runs its own color algorithm (`Button.algorithm`), which
// recomputes `colorBorder` from the seed and drops a global override, so the
// outline of every default button (the cart -/+ boxes, the trash button)
// would stay antd grey on any theme. Copy the theme's border color onto the
// Button's own border tokens, unless the editor set them explicitly.
export function withButtonBorder(theme) {
  const border = theme?.token?.colorBorder;
  const button = theme?.components?.Button;
  if (
    !border ||
    (button?.defaultBorderColor && button?.defaultBorderColorDisabled)
  ) {
    return theme;
  }
  return {
    ...theme,
    components: {
      ...theme.components,
      Button: {
        ...button,
        defaultBorderColor: button?.defaultBorderColor || border,
        defaultBorderColorDisabled:
          button?.defaultBorderColorDisabled || border,
      },
    },
  };
}

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

const hexToRgb = (hex) => {
  const h = hex.length === 4 ? hex.replace(/[0-9a-f]/gi, (c) => c + c) : hex;
  return [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
};

const rgbToHex = (rgb) =>
  "#" + rgb.map((v) => Math.round(v).toString(16).padStart(2, "0")).join("");

// `weight` of `into` blended into `hex`: mix("#000", "#fff", 0.5) is grey.
export function mixHex(hex, into, weight) {
  if (!HEX.test(hex || "") || !HEX.test(into || "")) return hex;
  const a = hexToRgb(hex);
  const b = hexToRgb(into);
  return rgbToHex(a.map((v, i) => v + (b[i] - v) * weight));
}

const isLight = (hex) => {
  const [r, g, b] = hexToRgb(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5;
};

// Two accents the editor does not derive from their parent color, so a shop
// that changes the primary or the card background but never opens the
// Slider or Cards section would keep the built-in green or navy:
//
// - Slider.trackBgDisabled, the price-range track while filters load, is a
//   tint of the primary. Left alone it stays green next to a pink primary.
// - Card.cardTicketGradient, the ticket-style card, is built from the card
//   background. Left alone it stays navy behind text that turned dark.
//
// Only the untouched default is replaced, and only when its parent changed,
// so a shop with no overrides renders the exact theme file (green track,
// navy ticket) and an explicit value from the editor always wins.
export function withDerivedAccents(theme) {
  if (!theme) return theme;
  const base = defaultTheme;
  const primary = theme?.token?.colorPrimary;
  const cardBg = theme?.components?.Card?.colorBgCard;
  const slider = theme?.components?.Slider;
  const card = theme?.components?.Card;
  let next = theme;

  if (
    primary !== base?.token?.colorPrimary &&
    HEX.test(primary || "") &&
    slider?.trackBgDisabled !== undefined &&
    slider?.trackBgDisabled === base?.components?.Slider?.trackBgDisabled
  ) {
    next = {
      ...next,
      components: {
        ...next.components,
        Slider: { ...slider, trackBgDisabled: mixHex(primary, "#ffffff", 0.55) },
      },
    };
  }

  if (
    cardBg !== base?.components?.Card?.colorBgCard &&
    HEX.test(cardBg || "") &&
    card?.cardTicketGradient !== undefined &&
    card?.cardTicketGradient === base?.components?.Card?.cardTicketGradient
  ) {
    const end = isLight(cardBg)
      ? mixHex(cardBg, "#000000", 0.08)
      : mixHex(cardBg, "#ffffff", 0.12);
    next = {
      ...next,
      components: {
        ...next.components,
        Card: {
          ...card,
          cardTicketGradient: `linear-gradient(to bottom, ${cardBg} 45%, ${end} 100%)`,
        },
      },
    };
  }

  return next;
}

// The merged theme for whatever shop settings are in Redux right now, plus
// the derived accents above. Pure: no DOM writes, no storage. Safe to call
// from any component.
export default function useShopTheme() {
  const overrides = useSelector(
    (state) => state?.shopDetails?.shop?.extra_config?.theme
  );
  return useMemo(
    () => withDerivedAccents(deepMerge(defaultTheme, overrides)),
    [overrides]
  );
}
